import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { ArrowRight, Compass, Flame, Package, Sparkles, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { useAuthStore } from '@/stores/authStore';
import { brand } from '@/lib/theme/tokens';
import { photo, unsplash } from '@/lib/utils/img';
import { cn } from '@/lib/utils/cn';

type Card = {
  kicker: string;
  kickerColor: string;
  titleHead: string;
  titleTail: string;
  gradient: string;
  body: string;
  image: string;
  accent: string;
  /** Optional badge icon row at the top of the visual. */
  badges: { icon: typeof Compass; label: string; color: string }[];
};

const CARDS: Card[] = [
  {
    kicker: 'Step 1 · Discover',
    kickerColor: brand.teal,
    titleHead: 'Find your',
    titleTail: 'Kashmir trip.',
    gradient: `linear-gradient(135deg, ${brand.teal}, ${brand.saffron})`,
    body:
      'Browse 9 activities, 17 stays, 6 houseboats, and 12 vehicles — all editorial, all bookable. Filter by region, difficulty, dates. We hold the inventory so the price you see is real.',
    image: photo.mountainLake,
    accent: brand.teal,
    badges: [
      { icon: Compass, label: 'Activities', color: brand.teal },
      { icon: Sparkles, label: 'Stays', color: brand.saffron },
    ],
  },
  {
    kicker: 'Step 2 · Connect',
    kickerColor: brand.chinar,
    titleHead: 'The trail is more fun',
    titleTail: 'with company.',
    gradient: `linear-gradient(135deg, ${brand.chinar}, ${brand.saffron})`,
    body:
      'Show up to recurring clubs, build a streak, match with companions doing your dates. Or virtually join an expedition that\'s live right now. This is TrailConnect — the social layer that makes solo trips feel like crew trips.',
    image: photo.alpineRidge,
    accent: brand.chinar,
    badges: [
      { icon: Flame, label: 'Clubs', color: brand.chinar },
      { icon: Users, label: 'Community', color: brand.teal },
    ],
  },
  {
    kicker: 'Step 3 · Travel light',
    kickerColor: brand.saffron,
    titleHead: 'Rent everything',
    titleTail: 'in the valley.',
    gradient: `linear-gradient(135deg, ${brand.saffron}, ${brand.chinar})`,
    body:
      'Skis, jackets, tents, drones, climbing kit, motorbikes — TrailKit holds the inventory. Don\'t fly with your gear. Pick up at the airport or your hotel, drop at any partner garage.',
    image: photo.skier,
    accent: brand.saffron,
    badges: [
      { icon: Package, label: 'TrailKit', color: brand.saffron },
    ],
  },
];

export function OnboardingModal() {
  const { isLoggedIn } = useAuthStore();
  const storeOpen = useOnboardingStore((s) => s.open);
  const finish = useOnboardingStore((s) => s.finish);
  const dismiss = useOnboardingStore((s) => s.dismiss);

  // Only show the modal to authenticated tourists.
  const open = isLoggedIn && storeOpen;
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  const card = CARDS[index]!;
  const isLast = index === CARDS.length - 1;

  function next() {
    if (isLast) {
      finish();
      navigate('/community');
    } else {
      setIndex((i) => i + 1);
    }
  }

  function skip() {
    finish();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) dismiss();
      }}
    >
      <DialogContent hideClose className="w-[min(880px,95vw)] max-w-none overflow-hidden p-0">
        <DialogTitle className="sr-only">Welcome to Uncharted Odyssey</DialogTitle>
        <DialogDescription className="sr-only">
          A 3-step tour of how to discover trips, connect with companions, and rent gear in Kashmir.
        </DialogDescription>
        <div className="grid grid-cols-1 md:grid-cols-[1.05fr_1fr]">
          {/* ── Visual side ─────────────────────────────────── */}
          <div className="relative min-h-[260px] md:min-h-[520px]">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={card.image}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0"
              >
                <img
                  src={unsplash(card.image, { w: 1200 })}
                  alt=""
                  aria-hidden
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(180deg, rgba(11,13,18,0.25) 0%, rgba(11,13,18,0.55) 60%, rgba(11,13,18,0.92) 100%)`,
                  }}
                />
                <div className="absolute left-5 top-5 flex flex-wrap gap-2 md:left-7 md:top-7">
                  {card.badges.map((b) => (
                    <span
                      key={b.label}
                      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white"
                      style={{ background: `${b.color}E0`, boxShadow: `0 8px 22px ${b.color}55` }}
                    >
                      <b.icon size={11} /> {b.label}
                    </span>
                  ))}
                </div>
                <div className="absolute inset-x-5 bottom-5 text-white md:inset-x-7 md:bottom-7">
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/80">
                    Uncharted Odyssey
                  </div>
                  <div className="font-display mt-1 text-[18px] font-semibold leading-tight md:text-[22px]">
                    Every Guest. Every Venue. One Platform.
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* ── Copy side ─────────────────────────────────── */}
          <div className="flex flex-col p-6 md:p-9">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={card.kicker}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                className="flex-1"
              >
                <div
                  className="text-[11px] font-bold uppercase tracking-[0.25em]"
                  style={{ color: card.kickerColor }}
                >
                  {card.kicker}
                </div>
                <h2 className="font-display mt-3 text-3xl font-semibold leading-[1.05] tracking-tight text-ink md:text-4xl">
                  {card.titleHead}{' '}
                  <em
                    className="font-medium italic"
                    style={{
                      display: 'inline-block',
                      lineHeight: 1.1,
                      paddingBottom: '0.1em',
                      backgroundImage: card.gradient,
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      color: 'transparent',
                    }}
                  >
                    {card.titleTail}
                  </em>
                </h2>
                <p className="mt-4 text-[14px] leading-relaxed text-ink-dim md:text-[14.5px]">
                  {card.body}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Progress dots */}
            <div className="mt-7 flex items-center gap-2">
              {CARDS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Step ${i + 1}`}
                  className={cn(
                    /* p-2.5 gives a 26px touch target around the 6px visual dot (WCAG 2.5.5) */
                    'rounded-full p-2.5 transition-all duration-300',
                  )}
                  style={{ lineHeight: 0 }}
                >
                  <span
                    className={cn(
                      'block h-1.5 rounded-full transition-all duration-300',
                      i === index ? 'w-7' : 'w-1.5 opacity-50',
                    )}
                    style={{
                      background: i <= index ? card.accent : 'var(--glass-edge-hi)',
                    }}
                  />
                </button>
              ))}
              <span className="ml-auto text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-mute">
                {index + 1} / {CARDS.length}
              </span>
            </div>

            {/* Actions */}
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <Button onClick={next} className="flex-1 sm:flex-initial">
                {isLast ? (
                  <>
                    Open Community <ArrowRight size={13} />
                  </>
                ) : (
                  <>
                    Next <ArrowRight size={13} />
                  </>
                )}
              </Button>
              {!isLast && (
                <Button variant="outline" size="md" onClick={skip}>
                  Skip tour
                </Button>
              )}
              {isLast && (
                <Button variant="outline" size="md" onClick={skip}>
                  Stay here
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
